import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Tag, Space,
  Popconfirm, message, Typography, Empty, Spin, Badge,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, CheckCircleOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import AppPageLayout from './common/AppPageLayout';
import PageHeader from './common/PageHeader';
import { EXECUTOR_API } from '../types';

const { Text } = Typography;
const { TextArea } = Input;

interface Connector {
  id: string;
  type: string;
  name: string;
  has_token: boolean;
  source: 'env' | 'db';
  created_at: string | null;
}

interface HttpConfig {
  url?: string;
  method?: string;
  description?: string;
  auth_mode?: 'none' | 'bearer' | 'api_key_header';
  auth_value?: string;
  auth_header?: string;
  body_template?: string;
  timeout?: number;
}

interface MondayConfig {
  api_token?: string;
}

type ConnectorConfig = HttpConfig & MondayConfig;

interface HttpFormValues {
  name: string;
  type: 'http';
  url: string;
  method: string;
  description: string;
  auth_mode: 'none' | 'bearer' | 'api_key_header';
  auth_value?: string;
  auth_header?: string;
  body_template?: string;
}

interface MondayFormValues {
  name: string;
  type: 'monday';
  api_token: string;
}

type FormValues = HttpFormValues | MondayFormValues | { name: string; type: string; api_token?: string };

const TYPE_LABELS: Record<string, { label: string; color: string }> = {
  monday: { label: 'Monday.com', color: '#0073ea' },
  http:   { label: 'HTTP API',   color: '#1677ff' },
  gmail:  { label: 'Gmail',      color: '#ea4335' },
  gdrive: { label: 'Google Drive', color: '#34a853' },
};

const ConnectorsPage: React.FC = () => {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConnector, setEditingConnector] = useState<Connector | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Watch the type field so the modal can render conditional inputs
  const selectedType = Form.useWatch('type', form);
  const selectedAuthMode = Form.useWatch('auth_mode', form);

  const fetchConnectors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/connectors`);
      if (res.ok) {
        const data = await res.json();
        setConnectors(data.connectors || []);
      }
    } catch {
      message.error('Error cargando conectores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConnectors(); }, [fetchConnectors]);

  const openCreate = () => {
    setEditingConnector(null);
    form.resetFields();
    form.setFieldsValue({ method: 'POST', auth_mode: 'none' });
    setModalOpen(true);
  };

  const openEdit = async (c: Connector) => {
    setEditingConnector(c);
    form.resetFields();

    // Fetch full config (sensitive fields redacted server-side)
    let cfg: ConnectorConfig = {};
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/connectors/${c.id}`);
      if (res.ok) {
        const data = await res.json();
        cfg = (data.config || {}) as ConnectorConfig;
      }
    } catch {
      // Ignore — user can still re-enter fields
    }

    if (c.type === 'http') {
      form.setFieldsValue({
        name: c.name,
        type: c.type,
        url: cfg.url || '',
        method: cfg.method || 'POST',
        description: cfg.description || '',
        auth_mode: cfg.auth_mode || 'none',
        auth_value: '', // always blank on edit; empty = keep existing
        auth_header: cfg.auth_header || '',
        body_template: cfg.body_template || '',
      });
    } else {
      form.setFieldsValue({ name: c.name, type: c.type, api_token: '' });
    }
    setModalOpen(true);
  };

  const buildConfig = (values: Record<string, unknown>, fallbackType?: string): ConnectorConfig => {
    const type = (values.type as string) || fallbackType;

    if (type === 'http') {
      const cfg: HttpConfig = {
        url: (values.url as string) || '',
        method: (values.method as string) || 'POST',
        description: (values.description as string) || '',
        auth_mode: (values.auth_mode as HttpConfig['auth_mode']) || 'none',
      };
      if (values.auth_value) cfg.auth_value = values.auth_value as string;
      if (values.auth_header) cfg.auth_header = values.auth_header as string;
      if (values.body_template) cfg.body_template = values.body_template as string;
      return cfg;
    }

    // monday / token-style
    const cfg: MondayConfig = {};
    if (values.api_token) cfg.api_token = values.api_token as string;
    return cfg;
  };

  const handleSave = async (values: FormValues) => {
    setSaving(true);
    try {
      const config = buildConfig(values as Record<string, unknown>, editingConnector?.type);

      if (editingConnector) {
        // On edit: always send the full config for HTTP (URL/method/etc may have changed),
        // but only include auth_value/api_token if user re-entered them.
        const body: Record<string, unknown> = { name: values.name };

        if (editingConnector.type === 'http') {
          // Preserve existing secrets if user left them blank
          const httpCfg = config as HttpConfig;
          if (!httpCfg.auth_value) {
            delete httpCfg.auth_value;
          }
          body.config = httpCfg;
        } else if ('api_token' in values && values.api_token) {
          body.config = { api_token: values.api_token };
        }

        const res = await fetch(`${EXECUTOR_API}/agent/builder/connectors/${editingConnector.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('Error actualizando conector');
        message.success('Conector actualizado');
      } else {
        const res = await fetch(`${EXECUTOR_API}/agent/builder/connectors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: values.type, name: values.name, config }),
        });
        if (!res.ok) throw new Error('Error creando conector');
        message.success('Conector creado');
      }

      setModalOpen(false);
      fetchConnectors();
    } catch {
      message.error('No se pudo guardar el conector');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/connectors/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      message.success('Conector eliminado');
      fetchConnectors();
    } catch {
      message.error('Error eliminando conector');
    }
  };

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type: string) => {
        const meta = TYPE_LABELS[type] || { label: type, color: '#1677ff' };
        return <Tag color={meta.color} style={{ fontWeight: 600 }}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'has_token',
      key: 'has_token',
      width: 160,
      render: (has: boolean, record: Connector) => (
        <Space size={6}>
          {has
            ? <Badge status="success" text={<Text style={{ fontSize: 12, color: '#52c41a' }}>Configurado</Text>} />
            : <Badge status="error" text={<Text style={{ fontSize: 12, color: '#ff4d4f' }}>Sin configurar</Text>} />}
          {record.source === 'env' && (
            <Tag style={{ fontSize: 10, marginLeft: 2 }}>ENV</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: Connector) => {
        if (record.source === 'env') return null;
        return (
          <Space size="small">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
            <Popconfirm
              title="¿Eliminar este conector?"
              onConfirm={() => handleDelete(record.id)}
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Effective type for rendering: when editing, take it from the connector;
  // otherwise from the form select.
  const effectiveType = editingConnector?.type || selectedType;

  return (
    <AppPageLayout maxWidth={900} padding={24}>
      <PageHeader
        icon={<ApiOutlined />}
        title="Conectores"
        subtitle="Integra herramientas externas. Los nodos LLM pueden usarlas como tools."
        action={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo conector
          </Button>
        }
      />

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : connectors.length === 0 ? (
          <Empty
            description="Sin conectores configurados"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Añadir conector
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={connectors.map(c => ({ ...c, key: c.id }))}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      <Modal
        title={editingConnector ? 'Editar conector' : 'Nuevo conector'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
        width={effectiveType === 'http' ? 640 : 520}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} style={{ marginTop: 16 }}>
          {!editingConnector && (
            <Form.Item name="type" label="Tipo" rules={[{ required: true }]}>
              <Select placeholder="Selecciona el tipo de conector">
                <Select.Option value="http">HTTP API (genérico)</Select.Option>
                <Select.Option value="monday">Monday.com</Select.Option>
                <Select.Option value="gmail" disabled>Gmail (próximamente)</Select.Option>
                <Select.Option value="gdrive" disabled>Google Drive (próximamente)</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item name="name" label="Nombre" rules={[{ required: true, max: 100 }]}>
            <Input placeholder={effectiveType === 'http' ? 'Ej: Cubicup Budget API' : 'Ej: Monday Cubicup'} />
          </Form.Item>

          {/* ── HTTP fields ── */}
          {effectiveType === 'http' && (
            <>
              <Form.Item
                name="description"
                label="Descripción de la herramienta"
                tooltip="Esto es lo que el LLM del agente lee para decidir cuándo invocar este endpoint. Sé específico."
                rules={[{ required: true, max: 500 }]}
              >
                <TextArea
                  rows={2}
                  placeholder="Ej: Crea un presupuesto en Cubicup a partir del resultado del cuestionario (quizInfo)."
                />
              </Form.Item>

              <div style={{ display: 'flex', gap: 8 }}>
                <Form.Item name="method" label="Método" style={{ width: 120 }} rules={[{ required: true }]}>
                  <Select>
                    <Select.Option value="GET">GET</Select.Option>
                    <Select.Option value="POST">POST</Select.Option>
                    <Select.Option value="PUT">PUT</Select.Option>
                    <Select.Option value="PATCH">PATCH</Select.Option>
                    <Select.Option value="DELETE">DELETE</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="url" label="URL" style={{ flex: 1 }} rules={[{ required: true, type: 'url' }]}>
                  <Input placeholder="https://api.cubicup.com/v1/budgets" />
                </Form.Item>
              </div>

              <Form.Item name="auth_mode" label="Autenticación" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="none">Sin autenticación</Select.Option>
                  <Select.Option value="bearer">Bearer token (Authorization)</Select.Option>
                  <Select.Option value="api_key_header">API key en header</Select.Option>
                </Select>
              </Form.Item>

              {selectedAuthMode === 'api_key_header' && (
                <Form.Item
                  name="auth_header"
                  label="Nombre del header"
                  rules={[{ required: true }]}
                  initialValue="X-API-Key"
                >
                  <Input placeholder="X-API-Key" />
                </Form.Item>
              )}

              {(selectedAuthMode === 'bearer' || selectedAuthMode === 'api_key_header') && (
                <Form.Item
                  name="auth_value"
                  label="Valor del token / API key"
                  rules={editingConnector ? [] : [{ required: true, message: 'Introduce el token' }]}
                  help={editingConnector ? 'Deja vacío para mantener el token actual' : undefined}
                >
                  <Input.Password placeholder={editingConnector ? '••••••••••••' : 'eyJhbGciOiJIUzI1NiJ9...'} />
                </Form.Item>
              )}

              <Form.Item
                name="body_template"
                label="Plantilla de body (opcional)"
                tooltip="Si la rellenas, el LLM solo pasa el valor `input` y se sustituye {{input}} en la plantilla. Si la dejas vacía, el LLM tendrá que construir el JSON completo cada vez."
              >
                <TextArea
                  rows={4}
                  placeholder={'Ej: {"transcript_data": {{input}}}'}
                />
              </Form.Item>
            </>
          )}

          {/* ── Monday (and other token-style) fields ── */}
          {effectiveType === 'monday' && (
            <>
              <Form.Item
                name="api_token"
                label="API Token"
                rules={editingConnector ? [] : [{ required: true, message: 'Introduce el token' }]}
                help={editingConnector ? 'Deja vacío para mantener el token actual' : undefined}
              >
                <Input.Password placeholder={editingConnector ? '••••••••••••' : 'eyJhbGciOiJIUzI1NiJ9...'} />
              </Form.Item>

              {!editingConnector && (
                <div style={{ marginBottom: 16, padding: '10px 12px', background: '#f6f8ff', borderRadius: 6, fontSize: 12, color: '#595959' }}>
                  <CheckCircleOutlined style={{ color: '#1677ff', marginRight: 6 }} />
                  Puedes probar la conexión en <strong>GET /health/monday</strong> desde la pestaña API del agente.
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editingConnector ? 'Guardar' : 'Crear conector'}
            </Button>
          </div>
        </Form>
      </Modal>
    </AppPageLayout>
  );
};

export default ConnectorsPage;
